<template>
  <div v-if="showModule" class="profile-module">
    <Toast position="bottom-center" />

    <div class="profile-shell" @wheel.stop>
      <div v-if="loading" class="profile-skeleton" aria-hidden="true">
        <section class="profile-skeleton-identity surface-card ">
          <Skeleton shape="circle" size="7rem" />
          <Skeleton width="11rem" height="1.2rem" borderRadius="999px" />
          <Skeleton width="8rem" height="0.95rem" borderRadius="999px" />
          <div class="profile-skeleton-chip-row">
            <Skeleton width="6rem" height="2rem" borderRadius="999px" />
            <Skeleton width="7rem" height="2rem" borderRadius="999px" />
          </div>
          <Skeleton width="100%" height="3rem" borderRadius="20px" />
          <Skeleton width="100%" height="3rem" borderRadius="20px" />
          <Skeleton width="100%" height="3rem" borderRadius="20px" />
        </section>

        <div class="profile-skeleton-main">
          <section
            v-for="index in 3"
            :key="index"
            class="profile-skeleton-panel surface-card "
          >
            <Skeleton width="10rem" height="1.1rem" borderRadius="999px" />
            <Skeleton width="18rem" height="0.9rem" borderRadius="999px" />
            <div class="profile-skeleton-grid">
              <div v-for="fieldIndex in 4" :key="fieldIndex" class="profile-skeleton-field">
                <Skeleton width="7rem" height="0.85rem" borderRadius="999px" />
                <Skeleton width="100%" height="3.25rem" borderRadius="18px" />
              </div>
            </div>
          </section>
        </div>
      </div>

      <div v-else-if="error" class="profile-state">
        <EmptyState
          :title="t('wkt.client.profile.error-state.title')"
          :description="t('wkt.client.profile.error-state.description')"
        />
        <Button
          class="profile-retry-button"
          :label="t('wkt.client.profile.actions.retry')"
          @click="loadProfile(true)"
        />
      </div>

      <div v-else-if="!profile" class="profile-state">
        <EmptyState
          :title="t('wkt.client.profile.error-state.title')"
          :description="t('wkt.client.profile.error-state.description')"
        />
      </div>

      <div v-else class="profile-layout">
        <aside class="profile-sidebar surface-card ">
          <div class="profile-sidebar-head">
            <div class="profile-avatar-shell">
              <img :src="avatarUrl" :alt="fullName" class="profile-avatar" />
              <div v-if="avatarUploading" class="profile-avatar-overlay">
                <span>{{ avatarProgress }}%</span>
              </div>
            </div>

            <Button
              v-if="canUploadAvatar"
              icon="pi pi-camera"
              :label="t('wkt.client.profile.photo.change')"
              text
              class="profile-upload-button"
              :disabled="avatarUploading"
              @click="openAvatarPicker"
            />

            <div class="profile-identity-copy">
              <h1 class="profile-name">{{ fullName }}</h1>
              <p class="profile-department">{{ profile.departmentName }}</p>

              <div class="profile-chip-row">
                <span class="profile-chip">{{ profile.companyName }}</span>
              </div>
            </div>
          </div>

          <div class="profile-sidebar-actions">
            <input
              ref="avatarInput"
              type="file"
              accept="image/*"
              class="profile-file-input"
              @change="onAvatarSelected"
            >

            <ProgressBar
              v-if="avatarUploading"
              :value="avatarProgress"
              class="profile-upload-progress"
            />
          </div>

          <section class="profile-sidebar-section">
            <dl class="profile-facts">
              <div class="profile-fact">
                <dt>{{ t("wkt.client.profile.access_code") }}</dt>
                <dd>
                  <span>{{ accessCode }}</span>
                  <button
                    v-if="showAccessCode"
                    type="button"
                    class="profile-inline-action"
                    :disabled="registerKeyBusy"
                    :aria-label="t('wkt.client.profile.access_code.refresh')"
                    @click="refreshRegisterKey"
                  >
                    <i class="pi pi-sync" aria-hidden="true" />
                  </button>
                </dd>
              </div>
              <div class="profile-fact">
                <dt>{{ t("wkt.client.profile.email") }}</dt>
                <dd>{{ profile.employee.email || "—" }}</dd>
              </div>
              <div class="profile-fact">
                <dt>{{ t("wkt.client.profile.phone") }}</dt>
                <dd>{{ displayPhone }}</dd>
              </div>
              <div class="profile-fact">
                <dt>{{ t("wkt.client.profile.id") }}</dt>
                <dd>{{ profile.employee.idNumber || "—" }}</dd>
              </div>
            </dl>
          </section>

        </aside>

        <main class="profile-main">
          <section class="profile-panel surface-card ">
            <div class="profile-panel-head">
              <div>
                <h2 class="profile-panel-title">{{ t("wkt.client.profile.section.preferences") }}</h2>
                <p class="profile-panel-description">
                  {{ profile.companyName }}
                </p>
              </div>
            </div>

            <div v-if="!hasDevice" class="profile-alert">
              <i class="pi pi-exclamation-triangle" aria-hidden="true" />
              <span>{{ t("wkt.client.profile.notifications_disabled_warning") }}</span>
            </div>

            <div class="profile-preferences-grid">
              <button
                type="button"
                class="profile-preference"
                :class="{ 'is-disabled': !hasDevice || toggleBusy.push }"
                :disabled="!hasDevice || toggleBusy.push"
                @click="togglePushNotifications"
              >
                <div class="profile-preference-copy">
                  <span class="profile-preference-label">
                    {{ t("wkt.client.profile.receive_push_notifications") }}
                  </span>
                  <span class="profile-preference-value">
                    {{ booleanLabel(Boolean(profile.device?.userNotification)) }}
                  </span>
                </div>
                <span class="profile-switch" :class="{ 'is-on': Boolean(profile.device?.userNotification) }" />
              </button>

              <button
                v-if="showSummaryPreference"
                type="button"
                class="profile-preference"
                :class="{ 'is-disabled': summaryBusy }"
                :disabled="summaryBusy"
                @click="toggleMonthlySummary"
              >
                <div class="profile-preference-copy">
                  <span class="profile-preference-label">
                    {{ t("wkt.client.profile.receive_monthly_summary") }}
                  </span>
                  <span class="profile-preference-value">
                    {{ booleanLabel(Boolean(profile.employee.sendMonthlySummary)) }}
                  </span>
                </div>
                <span class="profile-switch" :class="{ 'is-on': Boolean(profile.employee.sendMonthlySummary) }" />
              </button>

              <button
                v-if="showTeamCheckinPreference"
                type="button"
                class="profile-preference"
                :class="{ 'is-disabled': !hasDevice || toggleBusy.teamCheckin }"
                :disabled="!hasDevice || toggleBusy.teamCheckin"
                @click="toggleTeamCheckinNotifications"
              >
                <div class="profile-preference-copy">
                  <span class="profile-preference-label">
                    {{ t("wkt.client.profile.receive_checkin_notifications") }}
                  </span>
                  <span class="profile-preference-value">
                    {{ booleanLabel(Boolean(profile.device?.teamCheckinNotification)) }}
                  </span>
                </div>
                <span
                  class="profile-switch"
                  :class="{ 'is-on': Boolean(profile.device?.teamCheckinNotification) }"
                />
              </button>

              <div class="profile-preference profile-preference-setting">
                <div class="profile-preference-copy">
                  <span class="profile-preference-label">
                    {{ t("wkt.client.profile.locale") }}
                  </span>
                  <span class="profile-preference-value">
                    {{ currentLocaleLabel }}
                  </span>
                </div>

                <div class="profile-preference-control">
                  <Select
                    :modelValue="selectedLocale"
                    :options="localeOptions"
                    optionLabel="label"
                    optionValue="value"
                    class="profile-locale-select"
                    :disabled="localeBusy"
                    @update:modelValue="onLocaleSelected"
                  />
                  <p v-if="showReloadNotice" class="profile-locale-warning">
                    {{ t("wkt.client.profile.change-locale-warning") }}
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section v-if="showWhatsAppCard" class="profile-panel surface-card ">
            <div class="profile-panel-head">
              <div>
                <h2 class="profile-panel-title">{{ t("wkt.client.profile.whatsapp.title") }}</h2>
                <p class="profile-panel-description">{{ whatsappStatusText }}</p>
              </div>
            </div>

            <div v-if="whatsappLoading" class="profile-inline-skeleton">
              <Skeleton width="100%" height="3.2rem" borderRadius="18px" />
              <Skeleton width="9rem" height="2.8rem" borderRadius="999px" />
            </div>

            <template v-else>
              <div v-if="showOtpInput" class="profile-whatsapp-otp">
                <InputText
                  v-model="whatsappOtp"
                  inputmode="numeric"
                  maxlength="6"
                  :placeholder="t('wkt.client.profile.whatsapp.otp.label')"
                />
              </div>

              <div class="profile-whatsapp-actions">
                <Button
                  v-if="showWhatsappConnectButton"
                  icon="pi pi-whatsapp"
                  severity="success"
                  :label="t('wkt.client.profile.whatsapp.connect')"
                  :disabled="whatsappBusy"
                  @click="requestWhatsAppOtp"
                />
                <Button
                  v-if="showOtpInput"
                  icon="pi pi-check-circle"
                  :label="t('wkt.client.profile.whatsapp.verify')"
                  :disabled="whatsappBusy"
                  @click="verifyWhatsAppOtp"
                />
                <Button
                  v-if="showWhatsappDisconnectButton"
                  icon="pi pi-times-circle"
                  severity="secondary"
                  :label="t('wkt.client.profile.whatsapp.disconnect')"
                  :disabled="whatsappBusy"
                  @click="disconnectWhatsApp"
                />
              </div>
            </template>
          </section>

          <section class="profile-panel surface-card ">
            <div class="profile-panel-head">
              <div>
                <h2 class="profile-panel-title">{{ t("wkt.client.profile.section.shortcuts") }}</h2>
                <p class="profile-panel-description">
                  {{ t("wkt.client.profile.title") }}
                </p>
              </div>
            </div>

            <div class="profile-action-grid">
              <button
                v-for="action in quickActions"
                :key="action.id"
                type="button"
                class="profile-action-card"
                :class="{ 'profile-action-card-danger': action.id === 'logout' }"
                @click="action.run"
              >
                <span class="profile-action-label">{{ action.label }}</span>
              </button>
            </div>
          </section>

          <section v-if="showTeamSection" class="profile-panel surface-card ">
            <div class="profile-panel-head">
              <div>
                <h2 class="profile-panel-title">{{ t("wkt.client.profile.my_team") }}</h2>
                <p class="profile-panel-description">
                  {{ teamCountLabel }}
                </p>
              </div>

              <Button
                text
                icon="pi pi-calendar"
                :label="t('wkt.client.profile.view_team_vacations')"
                @click="gwtApi.profileGotoVacationTeam()"
              />
            </div>

            <div v-if="teamLoading" class="profile-team-skeleton">
              <div v-for="index in 4" :key="index" class="profile-team-skeleton-row">
                <Skeleton shape="circle" size="2.9rem" />
                <div class="profile-team-skeleton-copy">
                  <Skeleton width="9rem" height="0.9rem" borderRadius="999px" />
                  <Skeleton width="13rem" height="0.8rem" borderRadius="999px" />
                </div>
              </div>
            </div>

            <div v-else-if="teamError" class="profile-team-state">
              <span>{{ t("wkt.client.profile.error-state.description") }}</span>
              <Button
                text
                icon="pi pi-refresh"
                :label="t('wkt.client.profile.actions.retry')"
                @click="loadTeam"
              />
            </div>

            <div v-else-if="teamMembers.length === 0" class="profile-team-state">
              <span>{{ t("wkt.client.profile.no-data") }}</span>
            </div>

            <div v-else class="profile-team-list">
              <button
                v-for="member in teamMembers"
                :key="String(member.id ?? member.email ?? member.firstname ?? 'team-member')"
                type="button"
                class="profile-team-row"
                @click="openEmployeeDetails(member)"
              >
                <img
                  :src="getProfileAssetUrl(member.profilePicture)"
                  :alt="getTeamMemberName(member)"
                  class="profile-team-avatar"
                />
                <span class="profile-team-copy">
                  <span class="profile-team-name">{{ getTeamMemberName(member) }}</span>
                  <span class="profile-team-email">{{ member.email || "—" }}</span>
                </span>
                <i class="pi pi-angle-right profile-team-chevron" aria-hidden="true" />
              </button>
            </div>
          </section>
        </main>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { gwtApi } from "@wkt/api/gwt/gwtAPI";
import type { Device, Employee, SessionInformation } from "@wkt/api/gwt/gwtDTO";
import type {
  EmployeeSettingsDTO,
  GenericServiceResponseDTO,
  ProfileEmployeeDTO,
  WhatsAppStatusResponseDTO,
} from "@wkt/api/profileDTO";
import { worktimeApi } from "@wkt/api/worktime";
import EmptyState from "@wkt/components/common/EmptyState.vue";
import { useAlert } from "@wkt/composables/useAlert";
import { useUtils } from "@wkt/composables/useUtils";

const CORPORATE_LICENSE_TYPE = 2;
const PUSH_NOTIFICATIONS = 0;
const SUMMARY_NOTIFICATION = 1;
const TEAM_CHECKIN_NOTIFICATION = 2;
const MAX_AVATAR_SIZE_BYTES = 20 * 1024 * 1024;
const REGISTER_KEY_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890";
const AVAILABLE_LOCALES = ["es", "en", "it", "ro"];

interface ProfileViewModel {
  employee: Employee;
  company: NonNullable<SessionInformation["company"]>;
  device: Device | null;
  departmentName: string;
  companyName: string;
  isSupervisor: boolean;
  enabledProjectsCount: number;
  isKiosk: boolean;
}

interface QuickAction {
  id: string;
  label: string;
  run: () => void;
}

const { t, locale } = useI18n();
const { showAlert } = useAlert();
const { getProfileAssetUrl } = useUtils();

const showModule = ref(false);
const loading = ref(true);
const error = ref(false);
const profile = ref<ProfileViewModel | null>(null);
const selectedLocale = ref("es");
const showReloadNotice = ref(false);

const teamMembers = ref<ProfileEmployeeDTO[]>([]);
const teamLoading = ref(false);
const teamError = ref(false);

const whatsappStatus = ref<WhatsAppStatusResponseDTO | null>(null);
const whatsappLoading = ref(false);
const whatsappBusy = ref(false);
const whatsappOtp = ref("");
const whatsappAwaitingVerification = ref(false);
const whatsappUnavailable = ref(false);

const avatarInput = ref<HTMLInputElement | null>(null);
const avatarUploading = ref(false);
const avatarProgress = ref(0);

const localeBusy = ref(false);
const registerKeyBusy = ref(false);
const summaryBusy = ref(false);
const toggleBusy = ref({
  push: false,
  teamCheckin: false,
});

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function waitForSessionInformation(attempt = 0): Promise<SessionInformation | null> {
  const sessionInformation = gwtApi.getSessionInformation();
  if (sessionInformation?.employee && sessionInformation?.company) {
    return sessionInformation;
  }

  if (attempt >= 40) {
    return null;
  }

  await wait(250);
  return waitForSessionInformation(attempt + 1);
}

function normalizeSession(
  sessionInformation: SessionInformation,
  device: Device | null
): ProfileViewModel {
  const employee = { ...sessionInformation.employee };
  const company = { ...sessionInformation.company };
  const departmentName =
    sessionInformation.employeeDepartment?.name?.trim() || "—";
  const companyName = company.name?.trim() || "—";
  const enabledProjectsCount = Array.isArray(sessionInformation.employeeProjects)
    ? sessionInformation.employeeProjects.filter((project) => project?.enabled !== false).length
    : 0;

  return {
    employee,
    company,
    device: device ? { ...device } : null,
    departmentName,
    companyName,
    isSupervisor: Boolean(sessionInformation.isSupervisor),
    enabledProjectsCount,
    isKiosk: Boolean(window.localStorage.getItem("worktime.kiosk.employee.id")),
  };
}

function extractErrorMessage(errorValue: unknown, fallback: string): string {
  if (!errorValue || typeof errorValue !== "object") {
    return fallback;
  }

  const normalized = errorValue as {
    response?: { data?: unknown };
    message?: string;
  };

  if (typeof normalized.response?.data === "string" && normalized.response.data.trim()) {
    return normalized.response.data;
  }

  if (
    normalized.response?.data &&
    typeof normalized.response.data === "object" &&
    "message" in normalized.response.data
  ) {
    const message = (normalized.response.data as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) {
      return message;
    }
  }

  if (typeof normalized.message === "string" && normalized.message.trim()) {
    return normalized.message;
  }

  return fallback;
}

const hasDevice = computed(() => Boolean(profile.value?.device));
const canUploadAvatar = computed(() => Boolean(profile.value?.company.profilePhotoEnabled));
const showAccessCode = computed(() => Boolean(profile.value?.company.kioskEnabled));
const showSummaryPreference = computed(() => Boolean(profile.value?.company.sendEmployeeMonthlySummary));
const showTeamSection = computed(
  () => Boolean(profile.value?.isSupervisor) && profile.value?.company.checkinDayLimit == null
);
const showTeamCheckinPreference = computed(() => showTeamSection.value);
const showWhatsAppCard = computed(() => {
  return (
    Boolean(profile.value) &&
    profile.value?.company.licenseType === CORPORATE_LICENSE_TYPE &&
    Boolean(profile.value.company.whatsappEnabled)
  );
});
const isWhatsappEligible = computed(() => {
  if (!profile.value) {
    return false;
  }

  const employee = profile.value.employee;
  return Boolean(
    employee.mobileNumber?.trim() ||
      employee.phoneNumber?.trim()
  );
});
const isWhatsappVerified = computed(() => Boolean(whatsappStatus.value?.verified));
const showOtpInput = computed(
  () => showWhatsAppCard.value && isWhatsappEligible.value && !isWhatsappVerified.value && whatsappAwaitingVerification.value
);
const showWhatsappConnectButton = computed(
  () =>
    showWhatsAppCard.value &&
    isWhatsappEligible.value &&
    !isWhatsappVerified.value &&
    !whatsappUnavailable.value
);
const showWhatsappDisconnectButton = computed(
  () =>
    showWhatsAppCard.value &&
    isWhatsappEligible.value &&
    isWhatsappVerified.value &&
    !whatsappUnavailable.value
);

const fullName = computed(() => {
  const employee = profile.value?.employee;
  if (!employee) {
    return "---";
  }

  const firstname = employee.firstname?.trim() || employee.name?.trim() || "";
  const lastname = employee.lastname?.trim() || employee.surname?.trim() || "";
  const combined = `${firstname} ${lastname}`.trim();
  return combined || employee.email || "---";
});

const displayPhone = computed(() => {
  const employee = profile.value?.employee;
  if (!employee) {
    return "—";
  }

  const mobile = `${employee.mobileNumberPrefix || ""}${employee.mobileNumber || ""}`.trim();
  return mobile || employee.phoneNumber || "—";
});

const accessCode = computed(() => profile.value?.employee.registerKey || "—");
const avatarUrl = computed(() => getProfileAssetUrl(profile.value?.employee.profilePicture));

const localeOptions = computed(() =>
  AVAILABLE_LOCALES.map((value) => ({
    label: `${t(`wkt.client.locales.${value}`)} (${value})`,
    value,
  }))
);
const currentLocaleLabel = computed(() => {
  const match = localeOptions.value.find((option) => option.value === selectedLocale.value);
  return match?.label || selectedLocale.value;
});

const whatsappStatusText = computed(() => {
  if (!showWhatsAppCard.value) {
    return "";
  }

  if (!isWhatsappEligible.value) {
    return t("wkt.client.profile.whatsapp.status.phone_required");
  }

  if (!whatsappStatus.value) {
    return t("wkt.client.profile.whatsapp.status.not_connected");
  }

  if (!whatsappStatus.value.companyEnabled) {
    return t("wkt.client.profile.whatsapp.status.not_available");
  }

  if (whatsappUnavailable.value) {
    return t("wkt.client.profile.whatsapp.status.service_unavailable");
  }

  if (!whatsappStatus.value.verified) {
    return t("wkt.client.profile.whatsapp.status.not_connected");
  }

  const prefix = t("wkt.client.profile.whatsapp.status.connected");
  const formattedDate = formatWhatsAppDate(whatsappStatus.value.verifiedDate);
  const dateTemplate = t("wkt.client.profile.whatsapp.status.connected_date");
  const dateLabel = formattedDate ? ` ${dateTemplate.replace("%s", formattedDate)}` : "";
  const maskedPhone = whatsappStatus.value.maskedPhone?.trim()
    ? ` (${whatsappStatus.value.maskedPhone.trim()})`
    : "";

  return `${prefix}${dateLabel}${maskedPhone}`;
});

const teamCountLabel = computed(() => {
  return t("wkt.client.profile.my_team");
});

const quickActions = computed<QuickAction[]>(() => {
  if (!profile.value) {
    return [];
  }

  const actions: QuickAction[] = [
  ];

  if (profile.value.company.moduleHolidayEnabled) {
    actions.push({
      id: "vacations",
      label: t("wkt.client.profile.vacation_registration"),
      run: () => navigateTo("#vacations"),
    });
  }

  if (profile.value.company.moduleCheckinEnabled && profile.value.company.checkinDayLimit == null) {
    actions.push({
      id: "history",
      label: t("wkt.client.profile.checkin_historical"),
      run: () => gwtApi.profileGotoHistory(),
    });
  }

  if (profile.value.company.allowSeeTargetHours) {
    actions.push({
      id: "target-hours",
      label: t("wkt.client.profile.target_hours"),
      run: () => navigateTo("#targethours"),
    });
  }

  if (profile.value.enabledProjectsCount > 0) {
    actions.push({
      id: "projects",
      label: t("wkt.client.profile.show_my_projects"),
      run: () => navigateTo("#projects"),
    });
  }

  if (profile.value.company.trainingModuleEnabled) {
    actions.push({
      id: "training",
      label: t("wkt.client.profile.show_my_training"),
      run: () => navigateTo("#training"),
    });
  }

  actions.push(
    {
      id: "incidence",
      label: t("wkt.client.profile.send_comment_or_suggest"),
      run: () => gwtApi.profileSubmitIncidence(),
    },
    {
      id: "features",
      label: t("wkt.client.profile.view_features"),
      run: () => gwtApi.profileShowFeatureMessages(),
    },
    {
      id: "terms",
      label: t("wkt.client.profile.view_accepted_terms"),
      run: () => gwtApi.profileShowAcceptedTerms(),
    }
  );

  if (!profile.value.isKiosk) {
    actions.push({
      id: "logout",
      label: t("wkt.client.profile.logout"),
      run: () => gwtApi.profileCloseSession(),
    });
  }

  return actions;
});

function booleanLabel(value: boolean): string {
  return value ? t("wkt.client.common.yes") : t("wkt.client.common.no");
}

function formatWhatsAppDate(value?: string | null): string {
  if (!value) {
    return "";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat([locale.value, "en"], {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
}

function navigateTo(hash: string): void {
  window.location.hash = hash;
}

function getTeamMemberName(member: ProfileEmployeeDTO): string {
  const firstname = member.firstname?.trim() || "";
  const lastname = member.lastname?.trim() || "";
  const combined = `${firstname} ${lastname}`.trim();
  return combined || member.email || "---";
}

function openEmployeeDetails(member: ProfileEmployeeDTO): void {
  if (!member.id) {
    return;
  }

  gwtApi.profileShowEmployeeDetails(member.id);
}

function openAvatarPicker(): void {
  avatarInput.value?.click();
}

function onAvatarSelected(event: Event): void {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) {
    return;
  }

  if (!file.type.startsWith("image/")) {
    showAlert({
      text: t("wkt.client.profile.photo.invalid_type"),
      color: "warning",
    });
    input.value = "";
    return;
  }

  if (file.size > MAX_AVATAR_SIZE_BYTES) {
    showAlert({
      text: t("wkt.client.profile.photo.invalid_size"),
      color: "warning",
    });
    input.value = "";
    return;
  }

  avatarUploading.value = true;
  avatarProgress.value = 0;

  worktimeApi.uploadProfilePicture(
    file,
    (newPicture) => {
      avatarUploading.value = false;
      avatarProgress.value = 100;

      if (profile.value) {
        profile.value.employee.profilePicture = newPicture;
      }

      gwtApi.profileAvatarUpdated(newPicture);
      showAlert({
        text: t("wkt.client.profile.photo.upload_success"),
        color: "success",
      });

      input.value = "";
    },
    (uploadError) => {
      avatarUploading.value = false;
      avatarProgress.value = 0;
      showAlert({
        text: extractErrorMessage(
          uploadError,
          t("wkt.client.profile.error-state.description")
        ),
        color: "error",
      });
      input.value = "";
    },
    (progressValue) => {
      avatarProgress.value = progressValue;
    }
  );
}

function generateRegisterKey(): string {
  const randomValues = new Uint32Array(6);
  window.crypto.getRandomValues(randomValues);
  return Array.from(randomValues, (value) => REGISTER_KEY_CHARS[value % REGISTER_KEY_CHARS.length]).join("");
}

function refreshRegisterKey(): void {
  if (!profile.value || registerKeyBusy.value) {
    return;
  }

  const nextRegisterKey = generateRegisterKey();
  registerKeyBusy.value = true;

  worktimeApi.updateRegisterKey(
    nextRegisterKey,
    () => {
      registerKeyBusy.value = false;
      if (profile.value) {
        profile.value.employee.registerKey = nextRegisterKey;
      }
      showAlert({
        text: t("wkt.client.profile.access_code.updated"),
        color: "success",
      });
    },
    (requestError) => {
      registerKeyBusy.value = false;
      showAlert({
        text: extractErrorMessage(
          requestError,
          t("wkt.client.profile.error-state.description")
        ),
        color: "error",
      });
    }
  );
}

function onLocaleSelected(nextLocaleValue: string | null): void {
  if (!profile.value || !nextLocaleValue || nextLocaleValue === selectedLocale.value) {
    return;
  }

  selectedLocale.value = nextLocaleValue;
  localeBusy.value = true;

  const payload: EmployeeSettingsDTO = {
    locale: nextLocaleValue,
    timeZone: profile.value.employee.timeZone || null,
  };

  worktimeApi.updateEmployeeSettings(
    payload,
    () => {
      localeBusy.value = false;
      locale.value = nextLocaleValue;
      if (profile.value) {
        profile.value.employee.locale = nextLocaleValue;
      }
      showReloadNotice.value = true;
    },
    (requestError) => {
      localeBusy.value = false;
      selectedLocale.value = profile.value?.employee.locale || selectedLocale.value;
      showAlert({
        text: extractErrorMessage(
          requestError,
          t("wkt.client.profile.error-state.description")
        ),
        color: "error",
      });
    }
  );
}

function updateNotificationState(
  type: number,
  enable: boolean,
  busyKey: "push" | "teamCheckin",
  onSuccess: () => void
): void {
  toggleBusy.value = {
    ...toggleBusy.value,
    [busyKey]: true,
  };

  worktimeApi.updateNotifications(
    type,
    enable,
    () => {
      toggleBusy.value = {
        ...toggleBusy.value,
        [busyKey]: false,
      };
      onSuccess();
    },
    (requestError) => {
      toggleBusy.value = {
        ...toggleBusy.value,
        [busyKey]: false,
      };
      showAlert({
        text: extractErrorMessage(
          requestError,
          t("wkt.client.profile.error-state.description")
        ),
        color: "error",
      });
    }
  );
}

function togglePushNotifications(): void {
  if (!profile.value?.device) {
    return;
  }

  const nextValue = !Boolean(profile.value.device.userNotification);
  updateNotificationState(PUSH_NOTIFICATIONS, nextValue, "push", () => {
    if (profile.value?.device) {
      profile.value.device.userNotification = nextValue;
    }
  });
}

function toggleTeamCheckinNotifications(): void {
  if (!profile.value?.device) {
    return;
  }

  const nextValue = !Boolean(profile.value.device.teamCheckinNotification);
  updateNotificationState(TEAM_CHECKIN_NOTIFICATION, nextValue, "teamCheckin", () => {
    if (profile.value?.device) {
      profile.value.device.teamCheckinNotification = nextValue;
    }
  });
}

function toggleMonthlySummary(): void {
  if (!profile.value || summaryBusy.value) {
    return;
  }

  const nextValue = !Boolean(profile.value.employee.sendMonthlySummary);
  summaryBusy.value = true;

  worktimeApi.updateNotifications(
    SUMMARY_NOTIFICATION,
    nextValue,
    () => {
      summaryBusy.value = false;
      if (profile.value) {
        profile.value.employee.sendMonthlySummary = nextValue;
      }
    },
    (requestError) => {
      summaryBusy.value = false;
      showAlert({
        text: extractErrorMessage(
          requestError,
          t("wkt.client.profile.error-state.description")
        ),
        color: "error",
      });
    }
  );
}

function applyWhatsAppStatus(status: WhatsAppStatusResponseDTO | null): void {
  whatsappStatus.value = status;
  if (status?.verified) {
    whatsappAwaitingVerification.value = false;
    whatsappOtp.value = "";
  }
}

function loadWhatsAppStatus(): void {
  if (!showWhatsAppCard.value || !isWhatsappEligible.value) {
    whatsappStatus.value = null;
    whatsappLoading.value = false;
    whatsappAwaitingVerification.value = false;
    whatsappUnavailable.value = false;
    return;
  }

  whatsappLoading.value = true;
  whatsappUnavailable.value = false;

  worktimeApi.getWhatsAppStatus(
    (response) => {
      whatsappLoading.value = false;
      whatsappUnavailable.value = false;
      applyWhatsAppStatus(response ?? null);
    },
    () => {
      whatsappLoading.value = false;
      whatsappUnavailable.value = true;
      whatsappStatus.value = {
        companyEnabled: true,
        verified: false,
      };
      whatsappAwaitingVerification.value = false;
      whatsappOtp.value = "";
    }
  );
}

function showServiceResponse(response?: GenericServiceResponseDTO | WhatsAppStatusResponseDTO | null): void {
  const message = response?.message?.trim();
  if (!message) {
    return;
  }

  showAlert({
    text: message,
    color: response?.rsOK === false ? "warning" : "info",
  });
}

function requestWhatsAppOtp(): void {
  if (whatsappBusy.value) {
    return;
  }

  whatsappBusy.value = true;
  worktimeApi.requestWhatsAppOtp(
    (response) => {
      whatsappBusy.value = false;
      showServiceResponse(response);
      if (response?.rsOK) {
        whatsappAwaitingVerification.value = true;
      }
    },
    (requestError) => {
      whatsappBusy.value = false;
      showAlert({
        text: extractErrorMessage(
          requestError,
          t("wkt.client.profile.error-state.description")
        ),
        color: "error",
      });
    }
  );
}

function verifyWhatsAppOtp(): void {
  if (whatsappBusy.value) {
    return;
  }

  const normalizedOtp = whatsappOtp.value.trim();
  if (!/^\d{6}$/.test(normalizedOtp)) {
    showAlert({
      text: t("wkt.client.profile.whatsapp.otp.invalid"),
      color: "warning",
    });
    return;
  }

  whatsappBusy.value = true;
  worktimeApi.verifyWhatsAppOtp(
    { otpCode: normalizedOtp },
    (response) => {
      whatsappBusy.value = false;
      showServiceResponse(response);
      applyWhatsAppStatus(response ?? null);
      if (!response?.rsOK) {
        whatsappAwaitingVerification.value = true;
      }
    },
    (requestError) => {
      whatsappBusy.value = false;
      showAlert({
        text: extractErrorMessage(
          requestError,
          t("wkt.client.profile.error-state.description")
        ),
        color: "error",
      });
    }
  );
}

function disconnectWhatsApp(): void {
  if (whatsappBusy.value) {
    return;
  }

  whatsappBusy.value = true;
  worktimeApi.disconnectWhatsApp(
    (response) => {
      whatsappBusy.value = false;
      showServiceResponse(response);
      whatsappAwaitingVerification.value = false;
      whatsappOtp.value = "";
      loadWhatsAppStatus();
    },
    (requestError) => {
      whatsappBusy.value = false;
      showAlert({
        text: extractErrorMessage(
          requestError,
          t("wkt.client.profile.error-state.description")
        ),
        color: "error",
      });
    }
  );
}

function loadTeam(): void {
  if (!showTeamSection.value) {
    teamLoading.value = false;
    teamError.value = false;
    teamMembers.value = [];
    return;
  }

  teamLoading.value = true;
  teamError.value = false;

  worktimeApi.getMyTeam(
    (response) => {
      teamLoading.value = false;
      teamMembers.value = Array.isArray(response)
        ? [...response]
            .filter((member) => member?.isEnabled !== false)
            .sort((left, right) =>
              getTeamMemberName(left).localeCompare(getTeamMemberName(right), locale.value)
            )
        : [];
    },
    () => {
      teamLoading.value = false;
      teamError.value = true;
    }
  );
}

async function loadProfile(showTopLevelLoading = false): Promise<void> {
  showModule.value = true;

  if (showTopLevelLoading || !profile.value) {
    loading.value = true;
  }
  error.value = false;

  const sessionInformation = await waitForSessionInformation();
  if (!sessionInformation?.employee || !sessionInformation.company) {
    loading.value = false;
    error.value = true;
    return;
  }

  profile.value = normalizeSession(sessionInformation, gwtApi.getDevice());
  selectedLocale.value = profile.value.employee.locale || locale.value || "es";
  showReloadNotice.value = false;
  loading.value = false;

  if (showTeamSection.value) {
    loadTeam();
  } else {
    teamMembers.value = [];
    teamLoading.value = false;
    teamError.value = false;
  }

  loadWhatsAppStatus();
}

function onReveal(): void {
  void loadProfile(true);
}

function refreshModule(): void {
  void loadProfile(false);
}

defineExpose({
  onReveal,
  refreshModule,
});
</script>

<style scoped>
.profile-module {
  min-height: 100%;
  background: #fff;
}

.profile-shell {
  min-height: 100%;
  padding: 12px 24px 0;
}

.profile-layout,
.profile-skeleton {
  display: grid;
  grid-template-columns: minmax(280px, 360px) minmax(0, 1fr);
  gap: 24px;
  align-items: start;
}

.profile-sidebar,
.profile-panel,
.profile-skeleton-identity,
.profile-skeleton-panel {
  border-radius: 28px;
  border: 1px solid rgba(var(--primary-rgb), 0.08);
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(12px);
}

.profile-sidebar {
  padding: 28px;
  position: sticky;
  top: 24px;
}

.profile-sidebar-head {
  display: flex;
  flex-direction: column;
  gap: 18px;
  align-items: center;
  text-align: center;
}

.profile-avatar-shell {
  position: relative;
  width: 136px;
  height: 136px;
  border-radius: 32px;
  overflow: hidden;
  box-shadow: 0 18px 36px rgba(var(--primary-rgb), 0.18);
}

.profile-avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.profile-avatar-overlay {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  background: rgba(15, 23, 42, 0.58);
  color: #fff;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.profile-name {
  margin: 0;
  font-size: 1.9rem;
  line-height: 1.05;
  color: #10243a;
}

.profile-department {
  margin: 8px 0 0;
  color: #5c708a;
  font-size: 0.98rem;
}

.profile-chip-row,
.profile-skeleton-chip-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-top: 16px;
}

.profile-chip {
  border-radius: 999px;
  padding: 0.58rem 0.9rem;
  background: rgba(var(--primary-rgb), 0.1);
  color: var(--primary-dark-color);
  font-size: 0.85rem;
  font-weight: 600;
}

.profile-chip-strong {
  background: #10243a;
  color: #fff;
}

.profile-sidebar-actions {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.profile-upload-button {
  width: auto;
  padding-inline: 0;
  color: var(--primary-dark-color);
  font-weight: 600;
  margin-top: -4px;
}

.profile-upload-button:hover {
  background: transparent !important;
  color: var(--primary-dark-color) !important;
}

.profile-upload-button:focus-visible {
  outline: 2px solid rgba(var(--primary-rgb), 0.22);
  outline-offset: 4px;
}

.profile-upload-progress {
  width: 100%;
  margin-top: 12px;
}

.profile-file-input {
  display: none;
}

.profile-facts {
  margin: 0;
  display: grid;
  gap: 12px;
}

.profile-fact {
  display: grid;
  grid-template-columns: 1fr;
  gap: 5px;
  padding: 14px 16px;
  border-radius: 18px;
  background: #f8fbff;
}

.profile-fact dt {
  margin: 0;
  color: #698099;
  font-size: 0.8rem;
  font-weight: 600;
}

.profile-fact dd {
  margin: 0;
  color: #10243a;
  font-size: 0.97rem;
  font-weight: 600;
  word-break: break-word;
  display: flex;
  align-items: center;
  gap: 8px;
}

.profile-inline-action {
  border: 0;
  background: transparent;
  color: var(--primary-color);
  padding: 0;
  cursor: pointer;
}

.profile-locale-warning {
  margin: 10px 4px 0;
  color: #c2410c;
  font-size: 0.88rem;
}

.profile-main {
  display: grid;
  gap: 20px;
}

.profile-panel {
  padding: 24px;
}

.profile-panel-head {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 18px;
}

.profile-panel-title {
  margin: 0;
  color: #10243a;
  font-size: 1.2rem;
}

.profile-panel-description {
  margin: 6px 0 0;
  color: #6b7e95;
  font-size: 0.92rem;
}

.profile-alert {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  margin-bottom: 16px;
  border-radius: 18px;
  color: #9a3412;
  background: #fff1e8;
}

.profile-preferences-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
}

.profile-preference,
.profile-action-card,
.profile-team-row {
  border: 0;
  text-align: left;
  cursor: pointer;
}

.profile-preference {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 18px;
  border-radius: 22px;
  background: #f8fbff;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.profile-preference:hover,
.profile-action-card:hover,
.profile-team-row:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(var(--primary-rgb), 0.09);
}

.profile-preference.is-disabled {
  cursor: not-allowed;
  opacity: 0.68;
}

.profile-preference-setting {
  cursor: default;
  align-items: flex-start;
}

.profile-preference-setting:hover {
  transform: none;
  box-shadow: none;
}

.profile-preference-copy {
  display: grid;
  gap: 6px;
  min-width: 0;
}

.profile-preference-label {
  color: #10243a;
  font-weight: 600;
}

.profile-preference-value {
  color: #698099;
  font-size: 0.84rem;
  letter-spacing: 0.04em;
}

.profile-preference-control {
  width: min(100%, 180px);
  max-width: 100%;
  min-width: 0;
  display: grid;
  gap: 8px;
  flex-shrink: 0;
}

.profile-locale-select {
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.profile-preference-setting .profile-locale-warning {
  margin: 0;
}

.profile-switch {
  position: relative;
  flex-shrink: 0;
  width: 52px;
  height: 30px;
  border-radius: 999px;
  background: #cbd5e1;
  transition: background 0.2s ease;
}

.profile-switch::after {
  content: "";
  position: absolute;
  top: 4px;
  left: 4px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.2s ease;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.16);
}

.profile-switch.is-on {
  background: var(--primary-color);
}

.profile-switch.is-on::after {
  transform: translateX(22px);
}

.profile-action-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 14px;
}

.profile-action-card {
  width: 100%;
  padding: 18px;
  border-radius: 22px;
  background: linear-gradient(180deg, #ffffff 0%, #f7fbff 100%);
  display: block;
}

.profile-action-card-danger {
  border: 1px solid rgba(220, 38, 38, 0.14);
  background: linear-gradient(180deg, #fff 0%, #fff6f6 100%);
}

.profile-action-card-danger .profile-action-label {
  color: #b91c1c;
}

.profile-action-card-danger:hover {
  box-shadow: 0 10px 22px rgba(220, 38, 38, 0.12);
}

.profile-action-label {
  color: #10243a;
  font-weight: 600;
  line-height: 1.3;
}

.profile-whatsapp-otp {
  max-width: 280px;
  margin-bottom: 14px;
}

.profile-whatsapp-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.profile-team-list {
  display: grid;
  gap: 12px;
}

.profile-team-row {
  width: 100%;
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr) 18px;
  gap: 14px;
  align-items: center;
  padding: 14px 16px;
  border-radius: 20px;
  background: #f8fbff;
}

.profile-team-avatar {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 14px;
}

.profile-team-copy {
  display: grid;
  gap: 4px;
  min-width: 0;
}

.profile-team-name {
  color: #10243a;
  font-weight: 600;
}

.profile-team-email {
  color: #698099;
  font-size: 0.9rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.profile-team-chevron {
  color: #7a8ca1;
}

.profile-team-state,
.profile-state {
  min-height: 360px;
  display: grid;
  place-items: center;
  gap: 16px;
  text-align: center;
}

.profile-team-state {
  min-height: 0;
  justify-items: start;
  text-align: left;
}

.profile-inline-skeleton,
.profile-team-skeleton,
.profile-skeleton-main {
  display: grid;
  gap: 14px;
}

.profile-team-skeleton-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.profile-team-skeleton-copy {
  display: grid;
  gap: 8px;
  width: min(100%, 260px);
}

.profile-skeleton-identity {
  padding: 28px;
  display: grid;
  justify-items: center;
  gap: 14px;
}

.profile-skeleton-panel {
  padding: 24px;
  display: grid;
  gap: 14px;
}

.profile-skeleton-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}

.profile-skeleton-field {
  display: grid;
  gap: 8px;
}

.profile-retry-button {
  min-width: 180px;
}

@media (max-width: 1024px) {
  .profile-shell {
    padding: 18px;
  }

  .profile-layout,
  .profile-skeleton {
    grid-template-columns: 1fr;
  }

  .profile-sidebar {
    position: static;
  }
}

@media (max-width: 640px) {
  .profile-shell {
    padding: 0 12px 0;
  }

  .profile-sidebar,
  .profile-panel,
  .profile-skeleton-identity,
  .profile-skeleton-panel {
    border-radius: 22px;
  }

  .profile-sidebar,
  .profile-panel,
  .profile-skeleton-identity,
  .profile-skeleton-panel {
    padding: 18px;
  }

  .profile-avatar-shell {
    width: 112px;
    height: 112px;
    border-radius: 26px;
  }

  .profile-name {
    font-size: 1.58rem;
  }

  .profile-preferences-grid,
  .profile-action-grid {
    grid-template-columns: 1fr;
  }

  .profile-panel-head {
    flex-direction: column;
    align-items: stretch;
  }

  .profile-preference-setting {
    flex-direction: column;
    align-items: stretch;
  }

  .profile-preference-control {
    width: 100%;
  }

  .profile-whatsapp-actions :deep(.p-button) {
    width: 100%;
  }
}
</style>
